import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, AlertCircle, Camera, Loader2, ChevronDown, ChevronUp, RotateCcw, Settings, Mic } from "lucide-react";
import { AssessmentLayout } from "./AssessmentLayout";

interface WebcamSetupPageProps {
  onAccept: () => void;
  onDecline: () => void;
  companyName?: string;
  companyLogo?: string;
}

export function WebcamSetupPage({ onAccept, onDecline, companyName = "Company", companyLogo }: WebcamSetupPageProps) {
  // Camera states
  const [stream, setStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [troubleshootingOpen, setTroubleshootingOpen] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const videoRef = useRef<HTMLVideoElement>(null);

  // Audio states
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const [hasAudioPermission, setHasAudioPermission] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(true);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string>("");
  
  // Audio visualization
  const [audioLevel, setAudioLevel] = useState(0);
  const [stableAudioStatus, setStableAudioStatus] = useState<string>("No audio detected - try speaking");
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastStatusRef = useRef<string>("no_audio");
  const statusDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Check if browser supports required features
  const checkBrowserSupport = () => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  };

  useEffect(() => {
    const initializeCamera = async () => {
      if (!checkBrowserSupport()) {
        setError('Your browser doesn\'t support camera access. Please use a modern browser like Chrome, Firefox, or Safari.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        // Get available camera and microphone devices
        const deviceList = await navigator.mediaDevices.enumerateDevices();
        const cameras = deviceList.filter(device => device.kind === 'videoinput');
        const microphones = deviceList.filter(device => device.kind === 'audioinput');
        setDevices(cameras);
        setAudioDevices(microphones);
        
        // Set default audio device
        if (microphones.length > 0 && !selectedAudioDeviceId) {
          setSelectedAudioDeviceId(microphones[0].deviceId);
        }
        
        // Use selected device or default to first available
        const deviceId = selectedDeviceId || cameras[0]?.deviceId;
        if (deviceId && !selectedDeviceId) {
          setSelectedDeviceId(deviceId);
        }
        
        const constraints = {
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: "user",
            ...(deviceId && { deviceId: { exact: deviceId } })
          },
          audio: false
        };
        
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        
        setStream(mediaStream);
        streamRef.current = mediaStream;
        setHasPermission(true);
        
        // Ensure video element is available before setting stream
        if (videoRef.current) {
          (videoRef.current as any).srcObject = mediaStream;
          // Force play to ensure stream starts
          try {
            await videoRef.current.play();
          } catch (playError) {
            console.warn('Video play failed:', playError);
          }
        }
      } catch (err: any) {
        console.error('Camera access error:', err);
        let errorMessage = 'Failed to access camera. ';
        let actionText = 'Try Again';
        
        if (err.name === 'NotAllowedError') {
          errorMessage = 'Camera permission denied. Please click "Allow" when prompted, or check your browser settings.';
          actionText = 'Allow Camera Access';
        } else if (err.name === 'NotFoundError') {
          errorMessage = 'No camera found. Please connect a camera and try again.';
          actionText = 'Retry';
        } else if (err.name === 'NotReadableError') {
          errorMessage = 'Camera is being used by another application. Please close other apps using your camera.';
          actionText = 'Retry';
        } else if (err.name === 'OverconstrainedError') {
          errorMessage = 'Camera doesn\'t support the required settings. Try selecting a different camera.';
          actionText = 'Retry';
        } else {
          errorMessage += 'Please check your camera settings and permissions.';
        }
        
        setError(errorMessage);
        setHasPermission(false);
        setTroubleshootingOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    initializeCamera();

    return () => {
      const s = streamRef.current;
      if (s) {
        s.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [selectedDeviceId]);

  // Audio initialization
  useEffect(() => {
    const initializeAudio = async () => {
      if (!checkBrowserSupport()) {
        setAudioError('Your browser doesn\'t support microphone access.');
        setIsAudioLoading(false);
        return;
      }

      setIsAudioLoading(true);
      setAudioError(null);
      
      try {
        const constraints = {
          audio: selectedAudioDeviceId 
            ? { deviceId: { exact: selectedAudioDeviceId } }
            : true
        };
        
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        
        audioStreamRef.current = mediaStream;
        setAudioStream(mediaStream);
        setHasAudioPermission(true);
        
        // Set up audio visualization
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        
        const source = audioContext.createMediaStreamSource(mediaStream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;
        
        // Start audio level monitoring using RMS (Root Mean Square) for accurate voice detection
        let frameCount = 0;
        const updateAudioLevel = () => {
          if (!analyserRef.current) return;
          
          // Update every 2nd frame to reduce jitter
          frameCount++;
          if (frameCount % 2 === 0) {
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
            analyserRef.current.getByteTimeDomainData(dataArray);
            
            // Calculate RMS (Root Mean Square) - standard for audio level detection
            let sumSquares = 0;
            for (let i = 0; i < dataArray.length; i++) {
              const normalized = (dataArray[i] - 128) / 128; // Center around 0 (-1 to 1)
              sumSquares += normalized * normalized;
            }
            const rms = Math.sqrt(sumSquares / dataArray.length);
            
            // Apply sensitivity multiplier to make normal speech visible (RMS for speech is typically 0.05-0.2)
            const amplifiedLevel = Math.min(rms * 8, 1);
            
            // Apply exponential smoothing (EMA) to prevent jitter
            setAudioLevel(prev => prev * 0.7 + amplifiedLevel * 0.3);
          }
          
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        };
        
        updateAudioLevel();
        
      } catch (err: any) {
        console.error('Microphone access error:', err);
        let errorMessage = 'Failed to access microphone.';
        
        if (err.name === 'NotAllowedError') {
          errorMessage = 'Microphone permission denied. Please click "Allow" when prompted.';
        } else if (err.name === 'NotFoundError') {
          errorMessage = 'No microphone found. Please connect a microphone.';
        } else if (err.name === 'NotReadableError') {
          errorMessage = 'Microphone is in use by another app.';
        }
        
        setAudioError(errorMessage);
        setHasAudioPermission(false);
        setTroubleshootingOpen(true);
      } finally {
        setIsAudioLoading(false);
      }
    };

    initializeAudio();

    return () => {
      // Audio cleanup
      const audioS = audioStreamRef.current;
      if (audioS) {
        audioS.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
      
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [selectedAudioDeviceId]);

  // Separate effect to handle video stream assignment when ref becomes available
  useEffect(() => {
    if (stream && videoRef.current && hasPermission) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(err => {
        console.warn('Video play failed:', err);
      });
    }
  }, [stream, hasPermission]);

  const handleContinue = () => {
    if (hasPermission && hasAudioPermission && consentGiven) {
      // Stop video stream
      const s = streamRef.current;
      if (s) {
        s.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // Stop audio stream
      const audioS = audioStreamRef.current;
      if (audioS) {
        audioS.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
      
      // Close AudioContext
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      
      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      onAccept();
    }
  };

  const handleDecline = () => {
    // Stop video stream
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // Stop audio stream
    const audioS = audioStreamRef.current;
    if (audioS) {
      audioS.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    
    // Close AudioContext
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    // Cancel animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    onDecline();
  };

  const handleRetryPermission = async () => {
    // Reset state and retry camera access
    setTroubleshootingOpen(false);
    window.location.reload();
  };

  const handleDeviceChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
  };

  const handleAudioDeviceChange = (deviceId: string) => {
    setSelectedAudioDeviceId(deviceId);
  };

  // Get dynamic audio status based on level (with hysteresis thresholds)
  const getAudioStatus = (level: number) => {
    if (level < 0.05) return { 
      text: "No audio detected - try speaking", 
      color: "text-amber-600 dark:text-amber-400",
      icon: "🔇",
      key: "no_audio"
    };
    if (level < 0.12) return { 
      text: "Quiet - speak a bit louder", 
      color: "text-amber-600 dark:text-amber-400",
      icon: "🔈",
      key: "quiet"
    };
    if (level < 0.65) return { 
      text: "Audio level is good", 
      color: "text-green-600 dark:text-green-400",
      icon: "✓",
      key: "good"
    };
    if (level < 0.85) return { 
      text: "Slightly loud", 
      color: "text-amber-600 dark:text-amber-400",
      icon: "🔊",
      key: "loud"
    };
    return { 
      text: "Too loud - move away from mic", 
      color: "text-red-600 dark:text-red-400",
      icon: "⚠️",
      key: "too_loud"
    };
  };

  // Debounced status update to prevent flickering
  useEffect(() => {
    const status = getAudioStatus(audioLevel);
    
    if (status.key !== lastStatusRef.current) {
      // Clear existing debounce timer
      if (statusDebounceRef.current) {
        clearTimeout(statusDebounceRef.current);
      }
      
      // Debounce status changes by 200ms to prevent flickering
      statusDebounceRef.current = setTimeout(() => {
        setStableAudioStatus(status.text);
        lastStatusRef.current = status.key;
      }, 200);
    }
    
    return () => {
      if (statusDebounceRef.current) {
        clearTimeout(statusDebounceRef.current);
      }
    };
  }, [audioLevel]);

  // Get color/icon for the stable status
  const getStatusStyle = (statusText: string) => {
    if (statusText.includes("No audio")) return { color: "text-amber-600 dark:text-amber-400", icon: "🔇" };
    if (statusText.includes("Quiet")) return { color: "text-amber-600 dark:text-amber-400", icon: "🔈" };
    if (statusText.includes("good")) return { color: "text-green-600 dark:text-green-400", icon: "✓" };
    if (statusText.includes("Slightly")) return { color: "text-amber-600 dark:text-amber-400", icon: "🔊" };
    return { color: "text-red-600 dark:text-red-400", icon: "⚠️" };
  };

  // Animated Wave Visualizer Component
  const AudioWaveVisualizer = ({ level }: { level: number }) => {
    // 5 bars with different base heights to create wave shape
    const bars = [0.5, 0.8, 1, 0.8, 0.5];
    
    return (
      <div className="flex items-center gap-1 h-8 px-2">
        {bars.map((baseHeight, i) => {
          // Min height 15%, scales up with audio level
          const height = 15 + (level * baseHeight * 85);
          return (
            <div
              key={i}
              className="w-1.5 rounded-full bg-gradient-to-t from-green-500 to-emerald-400 transition-all duration-150"
              style={{ height: `${height}%` }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <AssessmentLayout companyName={companyName} companyLogo={companyLogo}>
      {/* Main Content - Centered Layout */}
      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 items-center min-h-[600px]">
            {/* Large Webcam Preview - Left Side */}
            <div className="flex flex-col items-center justify-center">
              <Card className="relative w-full max-w-2xl aspect-[4/3] overflow-hidden bg-gray-900 shadow-2xl">
                {isLoading ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                    <div className="flex flex-col items-center space-y-4 text-white">
                      <Loader2 className="h-12 w-12 animate-spin" />
                      <p className="text-lg">Accessing camera...</p>
                    </div>
                  </div>
                ) : error ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                    <div className="flex flex-col items-center space-y-4 text-white text-center px-6">
                      <Camera className="h-12 w-12 text-red-400" />
                      <p className="text-lg">{error}</p>
                      <Button 
                        onClick={handleRetryPermission}
                        variant="outline"
                        className="mt-4 bg-white/10 border-white/20 text-white hover:bg-white/20"
                        aria-label="Refresh page to retry camera access"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Refresh & Retry
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover scale-x-[-1]"
                      onLoadedMetadata={() => {
                        if (videoRef.current) {
                          videoRef.current.play().catch(err => {
                            console.warn('Video autoplay failed:', err);
                          });
                        }
                      }}
                      aria-label="Camera preview - you can see yourself as others will see you during the assessment"
                    />
                    {hasPermission && (
                      <div className="absolute top-4 left-4 flex items-center space-x-2 bg-blue-600 text-white px-3 py-1.5 rounded-full text-sm font-medium">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        <span>This interview will be recorded</span>
                      </div>
                    )}
                  </>
                )}
              </Card>
              
              {hasPermission && devices.length > 1 && (
                <div className="mt-4 flex items-center space-x-2">
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <select 
                    value={selectedDeviceId} 
                    onChange={(e) => handleDeviceChange(e.target.value)}
                    className="text-sm bg-background border border-border rounded px-3 py-1"
                    aria-label="Select camera device"
                  >
                    {devices.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Camera ${devices.indexOf(device) + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Microphone Test Section */}
              <Card className="mt-4 p-4 w-full max-w-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isAudioLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : audioError ? (
                      <Mic className="h-5 w-5 text-red-500" />
                    ) : hasAudioPermission ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Mic className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div>
                      <p className="font-medium">Microphone</p>
                      <p className="text-sm text-muted-foreground">
                        {isAudioLoading 
                          ? "Accessing microphone..." 
                          : audioError 
                          ? audioError 
                          : hasAudioPermission 
                          ? "Speak to test your microphone"
                          : "Waiting for permission..."}
                      </p>
                    </div>
                  </div>
                  
                {/* Audio Level Visualization */}
                {hasAudioPermission && !audioError && (
                  <AudioWaveVisualizer level={audioLevel} />
                )}
                </div>
                
                {/* Microphone Device Selector */}
                {hasAudioPermission && audioDevices.length > 1 && (
                  <div className="mt-3 flex items-center gap-2 pt-3 border-t">
                    <Settings className="h-4 w-4 text-muted-foreground" />
                    <select
                      value={selectedAudioDeviceId}
                      onChange={(e) => handleAudioDeviceChange(e.target.value)}
                      className="text-sm bg-background border border-border rounded px-3 py-1 flex-1"
                      aria-label="Select microphone device"
                    >
                      {audioDevices.map(device => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Microphone ${audioDevices.indexOf(device) + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </Card>
            </div>

            {/* Welcome Card - Right Side */}
            <div className="flex items-center justify-center">
              <Card className="w-full max-w-lg p-8 shadow-xl">
                <div className="space-y-6">
                  <div>
                    <h1 className="text-2xl font-bold text-foreground mb-4">
                      Camera Access Required
                    </h1>
                    <p className="text-muted-foreground leading-relaxed">
                      This assessment uses your camera. Your video may be recorded and reviewed by the hiring team to evaluate your submission. Please choose a quiet, well‑lit place and plan to complete the interview in one sitting. Your recording is stored securely and shared only with authorized reviewers. By continuing, you consent to camera use and recording.
                    </p>
                  </div>


                  {/* Consent Checkbox */}
                  <div className="pt-4">
                    <div className="flex items-start space-x-3 mb-6">
                      <Checkbox 
                        id="consent"
                        checked={consentGiven}
                        onCheckedChange={(checked) => setConsentGiven(checked === true)}
                        aria-describedby="consent-description"
                      />
                      <div className="text-sm">
                        <label 
                          htmlFor="consent" 
                          id="consent-description"
                          className="font-medium text-foreground cursor-pointer"
                        >
                          I understand and consent to being recorded during this assessment
                        </label>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <Button
                        onClick={handleContinue}
                        disabled={!hasPermission || !hasAudioPermission || !consentGiven}
                        className="w-full h-12 text-base font-medium bg-secondary hover:bg-secondary/90 text-white disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg"
                        aria-label={
                          !hasPermission 
                            ? "Camera access required to start assessment"
                            : !hasAudioPermission
                            ? "Microphone access required to start assessment"
                            : !consentGiven
                            ? "Please give consent to start assessment" 
                            : "Start assessment"
                        }
                      >
                        {!hasPermission 
                          ? "Camera Access Required" 
                          : !hasAudioPermission
                          ? "Microphone Access Required"
                          : !consentGiven 
                          ? "Consent Required"
                          : "Accept & Start Assessment"
                        }
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={handleDecline}
                        className="w-full h-12 text-base"
                        aria-label="Decline assessment and exit"
                      >
                        Decline & Exit
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Troubleshooting Section - Only show when needed */}
      {(!hasPermission || !hasAudioPermission) && !isLoading && !isAudioLoading && (
        <div className="max-w-4xl mx-auto px-6 pb-8">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>
                {!hasPermission && !hasAudioPermission 
                  ? "Camera and microphone permissions required" 
                  : !hasPermission 
                  ? "Camera permission required" 
                  : "Microphone permission required"}:
              </strong> Please allow access to continue with the assessment.
            </p>
          </div>
          
          <div className="border border-border rounded-lg">
            <button
              onClick={() => setTroubleshootingOpen(!troubleshootingOpen)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-accent/50 transition-colors"
              aria-expanded={troubleshootingOpen}
              aria-controls="troubleshooting-content"
            >
              <span className="font-medium">Having trouble with camera access?</span>
              {troubleshootingOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
            
            {troubleshootingOpen && (
              <div id="troubleshooting-content" className="p-4 pt-0 space-y-4 text-sm text-muted-foreground border-t border-border">
                {!hasPermission && (
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Camera troubleshooting:</h4>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Make sure your camera is connected and working</li>
                      <li>Check if another application is using your camera</li>
                      <li>Try refreshing the page and clicking "Allow" when prompted</li>
                      <li>Check your browser's camera permissions in settings</li>
                    </ul>
                  </div>
                )}
                
                {!hasAudioPermission && (
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Microphone troubleshooting:</h4>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Make sure your microphone is connected</li>
                      <li>Check if another application is using your microphone</li>
                      <li>Try selecting a different microphone from the dropdown</li>
                      <li>Speak aloud and check if the audio level meter responds</li>
                      <li>Check your browser's microphone permissions in settings</li>
                    </ul>
                  </div>
                )}
                
                <div>
                  <h4 className="font-medium text-foreground mb-2">Common solutions:</h4>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Try refreshing the page and clicking "Allow" when prompted</li>
                    <li>Try using a different browser (Chrome, Firefox, Safari)</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium text-foreground mb-2">Browser settings:</h4>
                  <ul className="space-y-1 list-disc list-inside">
                    <li><strong>Chrome:</strong> Click the camera icon in the address bar</li>
                    <li><strong>Firefox:</strong> Go to Settings → Privacy & Security → Permissions</li>
                    <li><strong>Safari:</strong> Go to Safari → Preferences → Websites → Camera</li>
                  </ul>
                </div>
                
                <Button 
                  onClick={handleRetryPermission}
                  variant="outline"
                  size="sm"
                  className="w-full mt-4"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Refresh Page & Retry
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </AssessmentLayout>
  );
}